import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    HelpCircle, Search, Users, Building2, CreditCard, ShieldCheck,
    Heart, Sparkles, ChevronRight,
} from "lucide-react";
import {
    Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/contexts/I18nContext";
import PublicHeader from "@/components/shared/PublicHeader";
import Footer from "@/components/Footer";

const HERO_IMAGE =
    "https://images.pexels.com/photos/4226140/pexels-photo-4226140.jpeg?auto=compress&cs=tinysrgb&w=1600";

function getCategories(lang) {
    return [
        {
            id: "members",
            Icon: Users,
            title: lang === "ar" ? "للأعضاء" : "For Members",
            blurb: lang === "ar" ? "كل ما تحتاج معرفته عن العضوية واستخدامها" : "Everything about your membership and how to use it",
            faqs: [
                {
                    q: lang === "ar" ? "كم تكلفة الاشتراك في حماية؟" : "How much does Hemaia cost?",
                    a: lang === "ar"
                        ? "التسعير بسيط وواضح: 500 جنيه سنوياً — رسوم واحدة، بدون رسوم مخفية أو مفاجآت. كلما استخدمت العضوية أكثر، استرديت قيمتها أسرع."
                        : "Simple, transparent pricing: 500 EGP / year — one flat fee, no hidden charges, no surprise bills. The more you use it, the faster it pays for itself.",
                },
                {
                    q: lang === "ar" ? "هل يمكنني استخدام حماية فوراً بعد الاشتراك؟" : "Can I use Hemaia immediately after subscribing?",
                    a: lang === "ar"
                        ? "نعم — فوراً بعد تأكيد الدفع. تجد بطاقتك الرقمية في حسابك وتستطيع الحجز في أي عيادة شريكة في نفس اليوم."
                        : "Yes — instantly. The moment your payment is confirmed, your digital card is active and you can book at any partner clinic the same day.",
                },
                {
                    q: lang === "ar" ? "هل يوجد حد لعدد مرات استخدام الخصومات؟" : "Is there a limit to how many times I can use discounts?",
                    a: lang === "ar"
                        ? "لا. تستفيد من الخصومات بشكل غير محدود طوال فترة الاشتراك السنوية في كل عيادة شريكة."
                        : "No. Use discounts as many times as you like throughout your annual subscription, at any partner provider.",
                },
                {
                    q: lang === "ar" ? "هل أدفع للعيادة مباشرة؟" : "Do I pay the clinic directly?",
                    a: lang === "ar"
                        ? "نعم، دائماً. تدفع مباشرة للعيادة أو المستشفى بالسعر المخفض. لا وسطاء، لا انتظار للتعويض."
                        : "Yes, always. You pay the provider on-site at the discounted rate. No intermediaries, no reimbursement delays.",
                },
                {
                    q: lang === "ar" ? "كيف أستخدم بطاقتي في العيادة؟" : "How do I use my card at the clinic?",
                    a: lang === "ar"
                        ? "أخبر مكتب الاستقبال بأنك عضو في حماية وأظهر بطاقتك الرقمية أو اذكر كود العضوية. سيتحقق الموظف ويُطبَّق الخصم على فاتورتك مباشرة."
                        : "Tell reception you're a Hemaia member and show your digital card or mention your member code. They'll verify and apply the discount to your bill on the spot.",
                },
                {
                    q: lang === "ar" ? "هل يمكنني تجديد العضوية؟" : "Can I renew my membership?",
                    a: lang === "ar"
                        ? "بالتأكيد. ستصلك تذكيرات قبل انتهاء الاشتراك بفترة كافية ويمكن التجديد بنقرة واحدة من حسابك."
                        : "Absolutely. You'll get reminders before your card expires and renewal is a single click from your account.",
                },
                {
                    q: lang === "ar" ? "كيف يعمل برنامج الإحالة؟" : "How does the referral program work?",
                    a: lang === "ar"
                        ? "شارك رابط الإحالة الخاص بك من حسابك. عندما يشترك صديق من خلاله، يحصل كلٌّ منكما على ٣٠ يوماً إضافياً مجاناً تُضاف لمدة العضوية."
                        : "Share your referral link from your account. When a friend signs up through it, both of you get an extra 30 days added to your membership — free.",
                },
            ],
        },
        {
            id: "providers",
            Icon: Building2,
            title: lang === "ar" ? "لمقدمي الخدمة" : "For Providers",
            blurb: lang === "ar" ? "لعيادات ومستشفيات وصيدليات تريد الانضمام للشبكة" : "For clinics, hospitals and pharmacies joining the network",
            faqs: [
                {
                    q: lang === "ar" ? "كيف أنضم إلى شبكة حماية كمقدم خدمة؟" : "How do I join the Hemaia network?",
                    a: lang === "ar"
                        ? "أنشئ حساباً عبر صفحة \"للمزودين\" وأكمل خطوات التحقق KYC. عادةً تستغرق المراجعة من ١ إلى ٢ يوم عمل، ثم تظهر عيادتك للأعضاء."
                        : "Create an account via the For Providers page and complete the KYC verification steps. Review typically takes 1–2 business days, after which your clinic goes live to members.",
                },
                {
                    q: lang === "ar" ? "هل توجد عمولة على كل زيارة؟" : "Are there commissions on each visit?",
                    a: lang === "ar"
                        ? "لا توجد عمولات على المعاملات في الباقة الحالية. ندعم النمو من خلال جلب أعضاء جدد ولا نأخذ نسبة على كل زيارة."
                        : "No per-visit commissions in the current package. We earn by bringing in new members, not by skimming every transaction.",
                },
                {
                    q: lang === "ar" ? "كيف يتم التحقق من الأعضاء عند زيارتنا؟" : "How are members verified at the visit?",
                    a: lang === "ar"
                        ? "موظفك يفتح صفحة \"التحقق\" ويبحث عن العضو بالاسم أو الهاتف أو كود العضوية، يحدد العرض المناسب، يُدخل قيمة الفاتورة، ويسجل الزيارة بنقرة واحدة."
                        : "Your staff opens the Verify page, looks the member up by name / phone / member code, picks the matching deal, enters the actual bill amount, and logs the visit in one tap.",
                },
                {
                    q: lang === "ar" ? "هل أستطيع إدارة فروع متعددة؟" : "Can I manage multiple branches?",
                    a: lang === "ar"
                        ? "نعم. يمكنك إضافة عدد غير محدود من الفروع، وتحديد الأطباء المرتبطين بكل فرع، وعروض مختلفة لكل فرع."
                        : "Yes. Add unlimited branches, assign experts to each branch, and run different offers per location.",
                },
                {
                    q: lang === "ar" ? "هل يمكنني تعديل العروض بعد الإطلاق؟" : "Can I edit offers after they go live?",
                    a: lang === "ar"
                        ? "نعم. التعديلات الطفيفة (الوصف، الصور) فورية. تغييرات السعر أو نسبة الخصم تتطلب مراجعة سريعة من فريقنا قبل أن تظهر للأعضاء."
                        : "Yes. Light edits (description, images) go live instantly. Price or discount-rate changes go through a quick review before they're visible to members.",
                },
            ],
        },
        {
            id: "billing",
            Icon: CreditCard,
            title: lang === "ar" ? "الدفع والفوترة" : "Payments & Billing",
            blurb: lang === "ar" ? "كيف ندير المدفوعات والاسترداد" : "How we handle payments and refunds",
            faqs: [
                {
                    q: lang === "ar" ? "ما طرق الدفع المتاحة؟" : "What payment methods do you accept?",
                    a: lang === "ar"
                        ? "نقبل التحويل البنكي حالياً. نعمل على إضافة الدفع بالبطاقة والمحافظ الإلكترونية قريباً."
                        : "Bank transfer is supported today. We're rolling out card and e-wallet payments soon.",
                },
                {
                    q: lang === "ar" ? "هل يمكنني استرداد قيمة الاشتراك؟" : "Can I get a refund?",
                    a: lang === "ar"
                        ? "نعم. خلال أول ١٤ يوماً وبدون أي زيارات مسجلة، يحق لك استرداد كامل المبلغ. تواصل مع الدعم وسننفذ الطلب خلال يومي عمل."
                        : "Yes. Within the first 14 days and with no recorded visits, you're entitled to a full refund. Contact support and we'll process it within 2 business days.",
                },
                {
                    q: lang === "ar" ? "هل يوجد رسوم خفية؟" : "Are there any hidden fees?",
                    a: lang === "ar"
                        ? "لا. ٥٠٠ ج.م سنوياً تشمل كل شيء: الوصول الكامل لكل العروض، تطبيق الويب والموبايل، ودعم العملاء."
                        : "No. 500 EGP / year covers everything: full access to all deals, web and mobile, and customer support.",
                },
            ],
        },
        {
            id: "security",
            Icon: ShieldCheck,
            title: lang === "ar" ? "الأمان والخصوصية" : "Security & Privacy",
            blurb: lang === "ar" ? "كيف نحمي بياناتك" : "How we protect your data",
            faqs: [
                {
                    q: lang === "ar" ? "هل بياناتي الطبية في أمان؟" : "Is my medical information safe?",
                    a: lang === "ar"
                        ? "حماية لا تخزن سجلاتك الطبية. نحفظ فقط معلومات العضوية الأساسية المطلوبة لتفعيل الخصم في العيادة. كل البيانات مشفرة أثناء النقل والتخزين."
                        : "Hemaia doesn't store your medical records. We only retain the essential membership info needed to apply your discount at the clinic. All data is encrypted in transit and at rest.",
                },
                {
                    q: lang === "ar" ? "هل العيادات الشريكة موثوقة؟" : "Are the partner clinics verified?",
                    a: lang === "ar"
                        ? "نعم، بدون استثناء. نتعامل فقط مع عيادات ومستشفيات مرخصة من وزارة الصحة المصرية، وكل مقدم خدمة يمر بعملية تحقق KYC قبل الانضمام."
                        : "Yes, without exception. We only work with clinics and hospitals licensed by the Egyptian Ministry of Health, and every provider passes a KYC verification before joining.",
                },
                {
                    q: lang === "ar" ? "ماذا يحدث إذا فقدت كود عضويتي؟" : "What if I lose my member code?",
                    a: lang === "ar"
                        ? "كود العضوية موجود دائماً في حسابك على لوحة التحكم وفي صفحة \"بطاقتي\". إذا فقدت الوصول للحساب نفسه، استخدم \"نسيت كلمة المرور\" أو تواصل مع الدعم."
                        : "Your member code is always in your account dashboard and on the \"My Card\" page. If you've lost access to the account itself, use \"Forgot password\" or contact support.",
                },
            ],
        },
    ];
}

export default function FAQPage() {
    const { lang, isRTL } = useI18n();
    const navigate = useNavigate();
    const [query, setQuery] = useState("");

    const categories = useMemo(() => getCategories(lang), [lang]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return categories;
        return categories
            .map((c) => ({
                ...c,
                faqs: c.faqs.filter(
                    (f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q)
                ),
            }))
            .filter((c) => c.faqs.length > 0);
    }, [categories, query]);

    const totalMatches = filtered.reduce((acc, c) => acc + c.faqs.length, 0);

    return (
        <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"} data-testid="page-faq">
            <PublicHeader />

            {/* Hero */}
            <section className="relative pt-24 pb-16 overflow-hidden">
                <div className="absolute inset-0">
                    <img src={HERO_IMAGE} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-b from-primary/85 via-primary/70 to-primary/60" />
                </div>
                <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
                    <div className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm border border-white/25 rounded-full px-3 py-1 text-xs font-semibold mb-5">
                        <HelpCircle className="h-3.5 w-3.5" />
                        {lang === "ar" ? "مركز المساعدة" : "Help Center"}
                    </div>
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-3">
                        {lang === "ar" ? "كيف يمكننا مساعدتك؟" : "How can we help?"}
                    </h1>
                    <p className="text-base text-white/85 max-w-md mx-auto mb-8">
                        {lang === "ar"
                            ? "إجابات لأكثر الأسئلة شيوعاً عن العضوية، التحقق، والدفع."
                            : "Answers to the most common questions about membership, verification, and billing."}
                    </p>
                    <div className="relative max-w-md mx-auto">
                        <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                        <Input
                            type="search"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={lang === "ar" ? "ابحث في الأسئلة..." : "Search the FAQ..."}
                            className="ps-10 h-11 bg-white text-foreground rounded-xl"
                            data-testid="faq-search"
                        />
                    </div>
                </div>
            </section>

            {/* Content */}
            <section className="py-16 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {query.trim() && (
                    <p className="text-sm text-muted-foreground mb-6 text-center">
                        {totalMatches === 0
                            ? (lang === "ar" ? "لا توجد نتائج. حاول كلمات أخرى أو راسلنا." : "No matches. Try different keywords or get in touch.")
                            : (lang === "ar" ? `${totalMatches} نتيجة` : `${totalMatches} matching answers`)}
                    </p>
                )}

                <div className="space-y-12">
                    {filtered.map((cat) => {
                        const Icon = cat.Icon;
                        return (
                            <div key={cat.id} id={cat.id} className="scroll-mt-24">
                                <div className="flex items-start gap-4 mb-5">
                                    <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                                        <Icon className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold tracking-tight">{cat.title}</h2>
                                        <p className="text-sm text-muted-foreground mt-0.5">{cat.blurb}</p>
                                    </div>
                                </div>
                                <Accordion type="single" collapsible className="space-y-2">
                                    {cat.faqs.map((faq, i) => (
                                        <AccordionItem
                                            key={`${cat.id}-${i}`}
                                            value={`${cat.id}-${i}`}
                                            className="border border-border/60 rounded-xl px-5 data-[state=open]:bg-secondary/20"
                                        >
                                            <AccordionTrigger className="text-start text-sm sm:text-base font-semibold hover:no-underline py-4">
                                                {faq.q}
                                            </AccordionTrigger>
                                            <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-4">
                                                {faq.a}
                                            </AccordionContent>
                                        </AccordionItem>
                                    ))}
                                </Accordion>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Still need help */}
            <section className="py-16 bg-secondary/20">
                <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <Sparkles className="h-8 w-8 text-primary mx-auto mb-3" />
                    <h2 className="text-2xl font-bold tracking-tight mb-2">
                        {lang === "ar" ? "لم تجد إجابة سؤالك؟" : "Didn't find your answer?"}
                    </h2>
                    <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                        {lang === "ar"
                            ? "فريق الدعم لدينا جاهز للرد على استفساراتك خلال ساعات العمل."
                            : "Our support team is ready to help during business hours."}
                    </p>
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                        <Button onClick={() => navigate("/contact")} className="rounded-xl px-5">
                            {lang === "ar" ? "تواصل معنا" : "Contact Support"}
                            <ChevronRight className="h-4 w-4 ms-1" />
                        </Button>
                        <Button onClick={() => navigate("/for-providers")} variant="outline" className="rounded-xl px-5">
                            <Heart className="h-4 w-4 me-1" />
                            {lang === "ar" ? "للمزودين" : "For Providers"}
                        </Button>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
